/**
 * Searchable multi-select for AWS conduit accounts.
 *
 * Renders a search box + grouped list (by department) with a checkbox per
 * account. Selected account IDs are surfaced as chips above the list. The
 * parent owns the canonical list of account IDs.
 */

import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Checkbox from '@amzn/meridian/checkbox';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import { AccountRecord } from '../service/adminApiService';

export interface AccountMultiSelectProps {
  accounts: AccountRecord[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export const AccountMultiSelect: React.FC<AccountMultiSelectProps> = ({
  accounts,
  value,
  onChange,
  disabled,
}) => {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      return (
        a.account_id.includes(q) ||
        a.account_name.toLowerCase().includes(q) ||
        a.department.toLowerCase().includes(q)
      );
    });
  }, [accounts, query]);

  const grouped = useMemo(() => {
    const byDept = new Map<string, AccountRecord[]>();
    for (const a of filtered) {
      const dept = a.department || '(no department)';
      const existing = byDept.get(dept) || [];
      existing.push(a);
      byDept.set(dept, existing);
    }
    return Array.from(byDept.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next).sort());
  };

  const selectAllVisible = () => {
    const next = new Set(selected);
    filtered.forEach((a) => next.add(a.account_id));
    onChange(Array.from(next).sort());
  };

  const clearAll = () => onChange([]);

  return (
    <Column spacing="small">
      <Row spacing="small" alignmentVertical="center">
        <div style={{ flex: 1 }}>
          <Input
            type="text"
            value={query}
            onChange={setQuery}
            placeholder="Search by account ID, name, or department…"
            aria-label="Search accounts"
          />
        </div>
        <Button type="tertiary" onClick={selectAllVisible} disabled={disabled || filtered.length === 0}>
          Select all visible
        </Button>
        <Button type="tertiary" onClick={clearAll} disabled={disabled || value.length === 0}>
          Clear
        </Button>
      </Row>
      <Text type="b200" color="secondary">
        {value.length} account{value.length === 1 ? '' : 's'} selected ({filtered.length} shown /{' '}
        {accounts.length} total)
      </Text>
      <div
        style={{
          maxHeight: '320px',
          overflowY: 'auto',
          border: '1px solid #D5DBDB',
          borderRadius: '4px',
          padding: '0.5rem',
        }}
        role="group"
        aria-label="Account selection"
      >
        {grouped.length === 0 && (
          <Text color="secondary">No accounts match the current search.</Text>
        )}
        {grouped.map(([dept, rows]) => (
          <Column key={dept} spacing="xsmall" spacingInset="200">
            <Text type="b300" color="secondary">
              {dept}
            </Text>
            {rows.map((a) => (
              <Checkbox
                key={a.account_id}
                checked={selected.has(a.account_id)}
                onChange={() => toggle(a.account_id)}
                disabled={disabled}
              >
                <Text type="b200">
                  {a.account_name || '(unnamed)'}{' '}
                  <span style={{ color: '#6B7A80' }}>
                    — {a.account_id}
                    {a.environment ? ` · ${a.environment}` : ''}
                  </span>
                </Text>
              </Checkbox>
            ))}
          </Column>
        ))}
      </div>
    </Column>
  );
};
